// src/pages/About.js
import React, { useEffect } from "react";

const About = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex items-center pt-24 md:pt-48 pb-12">
      <div className="w-full">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-[1154px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[auto_minmax(0,480px)] justify-center gap-12 lg:gap-16 xl:gap-24 items-start">
              {/* Image Column */}
              <div className="order-1">
                <div>
                  <div className="relative w-full aspect-[3/4] lg:aspect-auto lg:h-[min(56vh,38.7vw)] lg:w-[min(42vh,29vw)] bg-gray-900 overflow-hidden">
                    <img
                      src="/images/profile.jpg"
                      alt="Abdullah Kokash"
                      className="absolute inset-0 object-cover w-full h-full"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/800x1000/1a1a1a/ffffff?text=Abdullah+Kokash";
                      }}
                    />
                  </div>

                  {/* Social Links */}
                  <div className="mt-8 flex justify-center space-x-6">
                    <a
                      href="https://www.instagram.com/abdullahkokash"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:opacity-70 transition-opacity"
                      aria-label="Instagram"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                    <a
                      href="https://www.youtube.com/@akokash13"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:opacity-70 transition-opacity"
                      aria-label="YouTube"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 2H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3z"></path>
                        <path d="M10 9l5 3-5 3V9z"></path>
                      </svg>
                    </a>
                    <a
                      href="https://flickr.com/photos/190196764@N02/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:opacity-70 transition-opacity"
                      aria-label="Flickr"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="2" width="20" height="20" rx="2" ry="2"></rect>
                        <circle cx="9" cy="12" r="2.5"></circle>
                        <circle cx="15" cy="12" r="2.5"></circle>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Bio Column */}
              <div className="order-2">
                <div className="space-y-6 text-[15px] text-gray-300">
                  <p>
                    Abdullah Kokash is a Palestinian-Jordanian director and
                    cinematographer based in Brooklyn, New York, whose work
                    explores the experiences of Arabs and Palestinians in the
                    diaspora. His latest project, <i>To Beita, With Love</i>,
                    won the Grand Prize at the{" "}
                    <a
                      href="https://www.shorescripts.com/to-beita-with-love-pre-shoot-qa/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      Shore Scripts Short Film Fund
                    </a>
                    {" "}and was selected for{" "}
                    <a
                      href="https://flp.ps/node/2000"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      The Story Lab by Filmlab Palestine
                    </a>
                    . It also won{" "}
                    <a
                      href="https://www.instagram.com/p/DcwIdn0Efgp/?hl=el"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      Kashif's Screenplay for the People Award
                    </a>
                    , and will be produced as part of the Making Dope Shorts
                    program.
                  </p>

                  <p>
                    His film{" "}
                    <a
                      href="https://www.youtube.com/watch?v=0u0NHVU3VI8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      <i>Two Moons</i>
                    </a>
                    , shot across New York and Amman, has screened in over ten
                    countries, including at the Wikipedia for Peace summit in
                    Switzerland, the No More War festival in Bulgaria, and the
                    Transforming Narratives seminar in Norway, as well as venues
                    in Rome, the Netherlands, and Austria.{" "}
                    <a
                      href="https://seedandspark.com/fund/the-devils-veil#story"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      <i>The Devil's Veil</i>
                    </a>
                    , which raised over $8,000 through crowdfunding, is
                    currently in post-production.
                  </p>

                  <p>
                    Abdullah founded{" "}
                    <a
                      href="https://www.shortcutsnyc.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      Short Cuts
                    </a>
                    , a collective of independent filmmakers now{" "}
                    <a
                      href="https://www.nyfa.org/projects/project-info/?id=CA2043"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      fiscally sponsored by NYFA
                    </a>
                    {" "}and featured in{" "}
                    <a
                      href="https://www.nytimes.com/2026/08/01/arts/33-things-to-do-in-nyc-in-august.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-70 transition-opacity"
                    >
                      The New York Times
                    </a>
                    . A Brooklyn Arts Fund recipient, it has grown to 1,500
                    members and produced nearly 500 original short films
                    through monthly challenges.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
